/**
 * clientId — a stable per-device, per-browser-install identifier.
 *
 * Walter 2026-05-16: introduced for the per-device edit lock work
 * (extending the existing useEditLock from "soft lock for shared
 * lists" to "soft lock for ALL lists, including a solo user's
 * desktop vs phone"). The lock uses (uid, clientId) as the holder
 * identity — same uid + different clientId = "your other device is
 * editing this list," which the UI distinguishes from "another user
 * is editing this list."
 *
 * Persistence: localStorage. Survives reloads, PWA restarts,
 * cookie-clears that don't touch site data. Lost when the user
 * clears Safari Website Data or uninstalls the PWA — at which point
 * a fresh id is generated. That's fine; the lock's worst-case
 * behavior on id-change is a 30-second auto-release on the old id
 * (STALE_AFTER_MS in useEditLock).
 *
 * Cached in a module-scope variable so successive calls don't hit
 * localStorage repeatedly.
 */

const KEY = 'lists_client_id';

let _cached = null;

function generate() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  // Fallback for older runtimes / restricted contexts.
  return Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

export function getClientId() {
  if (_cached) return _cached;
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = generate();
      localStorage.setItem(KEY, id);
    }
    _cached = id;
    return id;
  } catch {
    // localStorage unavailable (private mode, quota). Use an in-memory
    // id for this session only. Two devices in the same situation will
    // collide on lock checks, but localStorage being unavailable is
    // edge-case enough that we accept it.
    if (!_cached) _cached = generate();
    return _cached;
  }
}
